import { AbstractClient, CompositionClient } from "@/app/client";
import { UUID } from "crypto";

export interface PartnerInfo {
  id: UUID;
  name: string;
}

export interface OperationInfo {
  id: UUID;
  operation_type: string;
  reference_number: string | null;
}

export interface FlowInfo {
  id: UUID;
  flow_type: string;
  quantity: number;
  weight: number;
  direction: string;
}

export interface TransferInfo {
  id: UUID;
  source_quantity: number;
  destination_quantity: number;
  status: string;
}

export interface TransactionResponse {
  id: UUID;
  company_id: UUID;
  operation_id: UUID | null;
  partner_id: UUID | null;
  operation: OperationInfo | null;
  partner: PartnerInfo | null;
  transaction_type: string;
  status: string;
  effective_date: string | null;
  description: string | null;
  transaction_metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface TransactionWithFlowsResponse extends TransactionResponse {
  flows: FlowInfo[];
  transfers: TransferInfo[];
}

export interface TransactionCreate {
  transaction_type: string;
  operation_id?: UUID | null;
  partner_id?: UUID | null;
  effective_date?: string | null;
  description?: string | null;
  transaction_metadata?: Record<string, any>;
}

export interface TransactionUpdate {
  transaction_type?: string;
  operation_id?: UUID | null;
  partner_id?: UUID | null;
  effective_date?: string | null;
  description?: string | null;
  transaction_metadata?: Record<string, any>;
}

export interface TransactionFilters {
  operation_id?: UUID;
  transaction_type?: string;
  status?: string;
  partner_id?: UUID;
}

export default class APITransactions extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  list = async (
    filters?: TransactionFilters,
  ): Promise<TransactionResponse[]> => {
    const params = new URLSearchParams();
    if (filters?.operation_id)
      params.append("operation_id", filters.operation_id);
    if (filters?.transaction_type)
      params.append("transaction_type", filters.transaction_type);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.partner_id) params.append("partner_id", filters.partner_id);

    const query = params.toString() ? `?${params.toString()}` : "";
    const res = await this._fetch({
      url: `/transactions/${query}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  listByOperation = async (
    operationId: UUID,
  ): Promise<TransactionResponse[]> => {
    const res = await this._fetch({
      url: `/transactions/by-operation/${operationId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  get = async (id: UUID): Promise<TransactionResponse> => {
    const res = await this._fetch({
      url: `/transactions/${id}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getWithFlows = async (id: UUID): Promise<TransactionWithFlowsResponse> => {
    const res = await this._fetch({
      url: `/transactions/${id}/with-flows`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  create = async (data: TransactionCreate): Promise<TransactionResponse> => {
    const res = await this._fetch({
      url: `/transactions/`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  update = async (
    id: UUID,
    data: TransactionUpdate,
  ): Promise<TransactionResponse> => {
    const res = await this._fetch({
      url: `/transactions/${id}`,
      method: "PATCH",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  delete = async (id: UUID): Promise<void> => {
    await this._fetch({
      url: `/transactions/${id}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  complete = async (id: UUID): Promise<TransactionResponse> => {
    const res = await this._fetch({
      url: `/transactions/${id}/complete`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  confirm = async (id: UUID): Promise<TransactionResponse> => {
    const res = await this._fetch({
      url: `/transactions/${id}/confirm`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  cancel = async (id: UUID): Promise<TransactionResponse> => {
    const res = await this._fetch({
      url: `/transactions/${id}/cancel`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };
}
